import React, { useState, useEffect } from 'react';
import { Box, Alert, AlertTitle, Chip } from '@mui/material';
import { Warning, CheckCircle } from '@mui/icons-material';

// Import all workspace components
import TypeScriptWorkspace from './TypeScriptWorkspace';
import JavaScriptWorkspace from './JavaScriptWorkspace';
import ReactWorkspace from './ReactWorkspace';
import VueWorkspace from './VueWorkspace';
import AngularWorkspace from './AngularWorkspace';
import NextJsWorkspace from './NextJsWorkspace';
import HtmlCssWorkspace from './HtmlCssWorkspace';

// Import workspace detector
import { detectWorkspaceType, validateWorkspaceMatch, getWorkspaceDisplayName, WORKSPACE_TYPES } from '../../utils/workspaceDetector';

/**
 * WorkspaceSelector Component
 * Automatically selects and renders the appropriate coding workspace based on question metadata
 */
function WorkspaceSelector({ question, expectedSkill, onCodeChange, initialCode }) {
  const [workspaceType, setWorkspaceType] = useState(null);
  const [validation, setValidation] = useState(null);

  useEffect(() => {
    if (question) {
      // Detect workspace type from question
      const detectedType = detectWorkspaceType(question);
      setWorkspaceType(detectedType);

      // Validate if expected skill matches detected workspace
      if (expectedSkill) {
        const validationResult = validateWorkspaceMatch(question, expectedSkill);
        setValidation(validationResult);

        // Log validation for debugging
        console.log('[WorkspaceSelector] Validation:', validationResult);

        if (!validationResult.valid) {
          console.warn('[WorkspaceSelector] Workspace mismatch detected:', validationResult.message);
        }
      }

      console.log('[WorkspaceSelector] Selected workspace:', detectedType, 'for question:', question);
    }
  }, [question, expectedSkill]);

  // Render appropriate workspace component
  const renderWorkspace = () => {
    const workspaceProps = {
      question,
      onCodeChange,
      initialCode,
    };

    switch (workspaceType) {
      case WORKSPACE_TYPES.TYPESCRIPT:
        return <TypeScriptWorkspace {...workspaceProps} />;

      case WORKSPACE_TYPES.JAVASCRIPT:
        return <JavaScriptWorkspace {...workspaceProps} />;

      case WORKSPACE_TYPES.REACT:
        return <ReactWorkspace {...workspaceProps} />;

      case WORKSPACE_TYPES.VUE:
        return <VueWorkspace {...workspaceProps} />;

      case WORKSPACE_TYPES.ANGULAR:
        return <AngularWorkspace {...workspaceProps} />;

      case WORKSPACE_TYPES.NEXTJS:
        return <NextJsWorkspace {...workspaceProps} />;

      case WORKSPACE_TYPES.HTML_CSS:
        return <HtmlCssWorkspace {...workspaceProps} />;

      default:
        // Fallback to HTML/CSS workspace
        console.warn('[WorkspaceSelector] Unknown workspace type:', workspaceType, '- using HTML/CSS fallback');
        return <HtmlCssWorkspace {...workspaceProps} />;
    }
  };

  if (!question) {
    return (
      <Alert severity="error">
        <AlertTitle>No Question Loaded</AlertTitle>
        Unable to determine workspace type. Question data is missing.
      </Alert>
    );
  }

  if (!workspaceType) {
    return (
      <Alert severity="warning">
        <AlertTitle>Loading Workspace...</AlertTitle>
        Detecting question type and loading appropriate coding environment.
      </Alert>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Workspace Status Header */}
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Chip
          icon={<CheckCircle />}
          label={`Workspace: ${getWorkspaceDisplayName(workspaceType)}`}
          color="primary"
          size="small"
        />

        {question._canonicalSkill && (
          <Chip
            label={`Skill: ${question._canonicalSkill}`}
            variant="outlined"
            size="small"
          />
        )}

        {question.difficulty && (
          <Chip
            label={`Level: ${question.difficulty}`}
            variant="outlined"
            size="small"
          />
        )}
      </Box>

      {/* Validation Warning (if mismatch detected) */}
      {validation && !validation.valid && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <AlertTitle>Workspace Validation Warning</AlertTitle>
          {validation.message}
          <br />
          <small>
            Expected: {getWorkspaceDisplayName(validation.expectedType)} |
            Detected: {getWorkspaceDisplayName(validation.detectedType)}
          </small>
        </Alert>
      )}

      {/* Render selected workspace */}
      <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
        {renderWorkspace()}
      </Box>
    </Box>
  );
}

export default WorkspaceSelector;
